import { hasLegalDocumentText, LegalDocumentKind } from '@provision/landing-page-utils';
import { useCallback, useMemo, useState } from 'react';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { isNullish } from 'remeda';
import { PENDING_DOCUMENT_ID } from 'src/graphql/entities/legal-document/legal-document-cache';
import type { LegalDocument } from 'src/graphql/entities/legal-document/legal-document.types';
import { useLegalDocuments } from 'src/graphql/entities/legal-document/use-legal-documents';
import { useManageLegalDocument } from 'src/graphql/entities/legal-document/use-manage-legal-document';
import type { LegalDocumentFormValues } from 'src/pages/Settings/memberships/components/legal-document/types';
import { useSeededForm } from 'src/pages/Settings/memberships/hooks/use-seeded-form';
import type { ConfigSurfaceController } from 'src/pages/Settings/memberships/types';
import { useConfirmationModal } from 'src/utils/hooks';
import { useNotifications } from 'src/utils/hooks/use-notifications';

export const NEW_DOCUMENT = 'new';

const DEFAULT_TITLES: Record<LegalDocumentKind, string> = {
  [LegalDocumentKind.TermsOfService]: 'Terms of Service',
  [LegalDocumentKind.PrivacyPolicy]: 'Privacy Policy',
};

const NEW_VERSION_SUFFIXES: Record<LegalDocumentKind, string> = {
  [LegalDocumentKind.TermsOfService]: 'Terms',
  [LegalDocumentKind.PrivacyPolicy]: 'Privacy Policy',
};

interface UseLegalDocumentSurfaceParams {
  kind: LegalDocumentKind;
  partnerId: number | null;
  selectedSegmentId: string | null;
  canManage: boolean;
}

export interface LegalDocumentSurface
  extends Omit<ConfigSurfaceController<LegalDocumentFormValues>, 'saveSlots'> {
  kind: LegalDocumentKind;
  formMethods: UseFormReturn<LegalDocumentFormValues>;
  /** Changes when another document loads; key the page editor with it. */
  formKey: string;
  data: LegalDocumentFormValues;
  documents: LegalDocument[];
  selectedDocumentId: string;
  savedDocument: LegalDocument | null;
  selectDocument: (documentId: string) => Promise<void>;
  /** Copies the segment's version, named for the segment, e.g. "Residential Terms". */
  startNewVersion: (segmentName: string) => void;
  deleteDocument: (documentId: string) => Promise<void>;
}

export const useLegalDocumentSurface = ({
  kind,
  partnerId,
  selectedSegmentId,
  canManage,
}: UseLegalDocumentSurfaceParams): LegalDocumentSurface => {
  const { alert } = useConfirmationModal();
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const { documents: allDocuments } = useLegalDocuments(partnerId, { skip: !canManage });
  const { saveDocument, assignDocument, deleteDocument, isSaving } =
    useManageLegalDocument(partnerId);

  // Keyed by segment, so switching segments drops a new document that was never saved.
  const [startingNew, setStartingNew] = useState<{ segmentId: string; name: string } | null>(
    null,
  );

  const documents = useMemo(
    () => allDocuments.filter((document) => document.kind === kind),
    [allDocuments, kind],
  );
  const savedDocument =
    documents.find(
      (document) => selectedSegmentId && document.brandIds.includes(selectedSegmentId),
    ) ?? null;
  const isStartingNew =
    Boolean(selectedSegmentId) && startingNew?.segmentId === selectedSegmentId;
  const isSavedDocumentPending = savedDocument?.legalDocumentId === PENDING_DOCUMENT_ID;
  const editedDocumentId =
    isStartingNew || isSavedDocumentPending ? null : (savedDocument?.legalDocumentId ?? null);

  const data = useMemo<LegalDocumentFormValues>(
    () =>
      savedDocument
        ? {
            name: isStartingNew
              ? (startingNew?.name ?? savedDocument.name)
              : savedDocument.name,
            title: savedDocument.title,
            bodyHtml: savedDocument.bodyHtml,
          }
        : { name: DEFAULT_TITLES[kind], title: DEFAULT_TITLES[kind], bodyHtml: '' },
    [savedDocument, isStartingNew, kind, startingNew],
  );

  const formKey = buildFormKey({
    kind,
    partnerId,
    segmentId: selectedSegmentId,
    documentId: editedDocumentId,
  });
  const { formMethods, isDirty } = useSeededForm(data, formKey);
  const bodyHtml = useWatch({ control: formMethods.control, name: 'bodyHtml' });
  // An empty body would publish a page with only a title.
  const hasBodyText = hasLegalDocumentText(bodyHtml);

  const hasUnsavedChanges = canManage && (isDirty || isStartingNew);

  const handleSave = useCallback(async () => {
    if (!canManage || !selectedSegmentId || isNullish(partnerId)) {
      return;
    }

    const isValid = await formMethods.trigger();

    if (!isValid) {
      await alert({
        title: 'Fix the highlighted fields',
        body: 'Some fields are invalid, so nothing was saved.',
        acknowledgeLabel: 'Got it',
      });
      return;
    }

    const values = formMethods.getValues();

    try {
      await saveDocument({
        brandId: selectedSegmentId,
        kind,
        legalDocumentId: editedDocumentId,
        ...values,
      });
      formMethods.reset(values);
      setStartingNew(null);
      showSuccessAlert(`${DEFAULT_TITLES[kind]} saved.`);
    } catch {
      showErrorAlert('Failed to save the document.');
    }
  }, [
    alert,
    canManage,
    editedDocumentId,
    formMethods,
    kind,
    partnerId,
    saveDocument,
    selectedSegmentId,
    showErrorAlert,
    showSuccessAlert,
  ]);

  const selectDocument = useCallback(
    async (documentId: string) => {
      if (!selectedSegmentId || documentId === PENDING_DOCUMENT_ID) return;

      setStartingNew(null);

      try {
        await assignDocument(selectedSegmentId, kind, documentId);
      } catch {
        showErrorAlert('Failed to switch documents.');
      }
    },
    [assignDocument, kind, selectedSegmentId, showErrorAlert],
  );

  const startNewVersion = useCallback(
    (segmentName: string) => {
      if (!selectedSegmentId) return;

      setStartingNew({
        segmentId: selectedSegmentId,
        name: `${segmentName} ${NEW_VERSION_SUFFIXES[kind]}`,
      });
    },
    [kind, selectedSegmentId],
  );

  const handleDelete = useCallback(
    async (documentId: string) => {
      try {
        await deleteDocument(documentId);
        showSuccessAlert('Version deleted.');
      } catch {
        showErrorAlert('Failed to delete the version.');
      }
    },
    [deleteDocument, showErrorAlert, showSuccessAlert],
  );

  return {
    kind,
    formMethods,
    formKey,
    data,
    documents,
    selectedDocumentId: editedDocumentId ?? NEW_DOCUMENT,
    savedDocument: isStartingNew ? null : savedDocument,
    selectDocument,
    startNewVersion,
    deleteDocument: handleDelete,
    hasUnsavedChanges,
    isSaving,
    isSaveDisabled: !canManage || isSaving || !hasBodyText,
    handleSave,
  };
};

function buildFormKey({
  kind,
  partnerId,
  segmentId,
  documentId,
}: {
  kind: LegalDocumentKind;
  partnerId: number | null;
  segmentId: string | null;
  documentId: string | null;
}): string {
  return [kind, String(partnerId), segmentId ?? '', documentId ?? NEW_DOCUMENT].join(':');
}
